
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Transaction, CreditCard, WealthItem } from '../types';
import { useAuth } from './AuthContext';
import { useVault } from './VaultContext';
import { sheetsService } from '../utils/googleSheets';
import { googleAuth } from '../utils/googleAuth';

interface DataContextType {
  transactions: Transaction[];
  cards: CreditCard[];
  wealthItems: WealthItem[];
  addTransaction: (t: Transaction | Transaction[]) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;
  updateTransaction: (t: Transaction) => Promise<void>;
  addCard: (c: CreditCard) => Promise<void>;
  updateCard: (c: CreditCard) => Promise<void>;
  deleteCard: (id: string) => Promise<void>;
  saveWealthItem: (i: WealthItem) => Promise<void>;
  deleteWealthItem: (id: string) => Promise<void>;
  clearTransactions: (start: Date, end: Date) => Promise<void>;
  repairData: () => Promise<void>;
  loadAllData: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { spreadsheetId, userStatus, setLoading } = useAuth();
  const { isVaultLocked } = useVault();
  
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [cards, setCards] = useState<CreditCard[]>([]);
  const [wealthItems, setWealthItems] = useState<WealthItem[]>([]);

  const loadAllData = useCallback(async () => {
    if (!spreadsheetId || isVaultLocked) return;
    
    setLoading(true, 'Buscando registros...');
    try {
      const data = await sheetsService.fetchData(spreadsheetId);
      if (data) {
        setTransactions(data.transactions || []);
        setCards(data.cards || []);
        setWealthItems(data.wealth || []);
      }
    } catch (e) {
      console.error('Error loading data:', e);
    } finally {
      setLoading(false);
    }
  }, [spreadsheetId, isVaultLocked, setLoading]);

  useEffect(() => {
    if (spreadsheetId && !isVaultLocked) {
      loadAllData();
    }
  }, [spreadsheetId, isVaultLocked, loadAllData]);

  const addTransaction = useCallback(async (t: Transaction | Transaction[]) => {
    const newItems = Array.isArray(t) ? t : [t];
    setTransactions(prev => {
      const updated = [...newItems, ...prev];
      if (spreadsheetId) {
        // Usamos syncTab em vez de saveTransactionsBatch para garantir integridade das colunas
        sheetsService.syncTab(spreadsheetId, 'Transactions', updated).catch(console.error);
      }
      return updated;
    });
  }, [spreadsheetId]);

  const deleteTransaction = useCallback(async (id: string) => {
    const transactionToDelete = transactions.find(t => t.id === id);
    
    if (transactionToDelete?.wealthItemId) {
      setWealthItems(prevWealth => {
        const updatedWealth = prevWealth.map(item => {
          if (item.id === transactionToDelete.wealthItemId) {
            // Reverte o aporte (subtrai o valor se era uma despesa/aporte)
            return { ...item, amount: item.amount - transactionToDelete.amount };
          }
          return item;
        });
        if (spreadsheetId) {
          sheetsService.syncTab(spreadsheetId, 'Wealth', updatedWealth).catch(console.error);
        }
        return updatedWealth;
      });
    }

    setTransactions(prev => {
      const updated = prev.filter(t => t.id !== id);
      if (spreadsheetId) {
        sheetsService.syncTab(spreadsheetId, 'Transactions', updated).catch(console.error);
      }
      return updated;
    });
  }, [spreadsheetId, transactions]);

  const updateTransaction = useCallback(async (ut: Transaction) => {
    setTransactions(prev => {
      const updated = prev.map(t => t.id === ut.id ? ut : t);
      if (spreadsheetId) {
        sheetsService.syncTab(spreadsheetId, 'Transactions', updated).catch(console.error);
      }
      return updated;
    });
  }, [spreadsheetId]);

  const addCard = useCallback(async (c: CreditCard) => {
    setCards(prev => {
      const updated = [...prev, c];
      if (spreadsheetId) {
        sheetsService.syncTab(spreadsheetId, 'Cards', updated).catch(console.error);
      }
      return updated;
    });
  }, [spreadsheetId]);

  const updateCard = useCallback(async (c: CreditCard) => {
    setCards(prev => {
      const updated = prev.map(x => x.id === c.id ? c : x);
      if (spreadsheetId) {
        sheetsService.syncTab(spreadsheetId, 'Cards', updated).catch(console.error);
      }
      return updated;
    });
  }, [spreadsheetId]);

  const deleteCard = useCallback(async (id: string) => {
    setCards(prev => {
      const updated = prev.filter(x => x.id !== id);
      if (spreadsheetId) {
        sheetsService.syncTab(spreadsheetId, 'Cards', updated).catch(console.error);
      }
      return updated;
    });
  }, [spreadsheetId]);

  const saveWealthItem = useCallback(async (i: WealthItem) => {
    setWealthItems(prev => {
      const exists = prev.find(x => x.id === i.id);
      const updated = exists ? prev.map(x => x.id === i.id ? i : x) : [...prev, i];
      if (spreadsheetId) {
        sheetsService.syncTab(spreadsheetId, 'Wealth', updated).catch(console.error);
      }
      return updated;
    });
  }, [spreadsheetId]);

  const deleteWealthItem = useCallback(async (id: string) => {
    setWealthItems(prev => {
      const updated = prev.filter(x => x.id !== id);
      if (spreadsheetId) {
        sheetsService.syncTab(spreadsheetId, 'Wealth', updated).catch(console.error);
      }
      return updated;
    });
  }, [spreadsheetId]);

  const clearTransactions = useCallback(async (start: Date, end: Date) => {
    setLoading(true, 'Limpando registros...');
    try {
      setTransactions(prev => {
        const remaining = prev.filter(t => {
          const d = new Date(t.date + 'T12:00:00');
          return d < start || d > end;
        });
        if (spreadsheetId) {
          sheetsService.syncTab(spreadsheetId, 'Transactions', remaining).catch(console.error);
        }
        return remaining;
      });
    } catch (e) {
      console.error('Error clearing transactions:', e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [spreadsheetId, setLoading]);

  const repairData = useCallback(async () => {
    if (!spreadsheetId) return;
    setLoading(true, 'Reparando banco de dados...');
    try {
      // Primeiro atualiza os cabeçalhos para garantir que as colunas novas existam
      const token = await googleAuth.getGoogleToken();
      if (token) {
        await sheetsService.setupHeaders(spreadsheetId, token);
      }

      await Promise.all([
        sheetsService.syncTab(spreadsheetId, 'Transactions', transactions),
        sheetsService.syncTab(spreadsheetId, 'Cards', cards),
        sheetsService.syncTab(spreadsheetId, 'Wealth', wealthItems)
      ]);
      await loadAllData();
    } catch (e) {
      console.error('Error repairing data:', e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [spreadsheetId, transactions, cards, wealthItems, loadAllData, setLoading]);

  return (
    <DataContext.Provider value={{
      transactions,
      cards,
      wealthItems,
      addTransaction,
      deleteTransaction,
      updateTransaction,
      addCard,
      updateCard,
      deleteCard,
      saveWealthItem,
      deleteWealthItem,
      clearTransactions,
      repairData,
      loadAllData
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
